import type { ErrorEvent, EventHint, Breadcrumb } from '@sentry/nextjs'

const SENSITIVE_HEADER = /^(?:authorization|cookie|set-cookie|x-.*-(?:token|secret|signature)|stripe-signature|sanity-webhook-signature|svix-signature|x-shopify-hmac-sha256|x-anet-signature|x-patreon-signature)$/i

const SENSITIVE_KEY = /^(?:.*_)?(?:email|phone|address|first_name|last_name|name|display_name|card|card_number|cvv|cvv2|token|access_token|refresh_token|api_key|apikey|secret|password|authorization|cookie|set_cookie|signature)$/i

const REDACTED = '[REDACTED]'

/**
 * Benign auth/session errors that are noise, not bugs — dropped before they
 * reach Sentry (and reported at `warn`, not `error`, when we log them
 * ourselves). "Invalid Refresh Token: Refresh Token Not Found" is thrown by
 * the Supabase SDK during a routine token refresh whenever the refresh token
 * has rotated, expired, or was cleared (logged-out or long-idle visitors) —
 * it is the expected outcome of a stale session, resolved by re-authenticating,
 * and never something we act on. Shared across the server, edge, and client
 * Sentry configs.
 *
 * Matched on the FULL message on purpose. GoTrue emits other
 * "Invalid Refresh Token: …" variants — notably "…: Already Used"
 * (refresh_token_already_used), which can indicate a concurrent-refresh race
 * OR refresh-token replay/theft. Those must stay visible, so we do NOT match
 * the bare "Invalid Refresh Token" prefix.
 */
export const SENTRY_IGNORE_ERRORS: RegExp[] = [
  /Invalid Refresh Token: Refresh Token Not Found/i,
  // CSP reports for infird.com — a known adware/extension domain whose own
  // injected script loads more of itself; the (enforced) CSP blocks it, which
  // is the system working. The pattern is pinned to this exact blocked host
  // so genuinely new violation shapes keep reporting; the csp_violations
  // Supabase aggregate still records these for the admin Security panel.
  /^CSP violation: script-src-elem blocked https:\/\/infird\.com$/,
]

// Recurse through arrays too. Previously arrays fell straight to the else
// branch and were copied verbatim, so PII nested inside one (e.g.
// `recipients: [{ email }]`, `items: [{ card_number }]`) bypassed redaction
// entirely. We walk each element so a sensitive key is caught no matter how
// deep, or inside how many array layers, it sits.
function scrubValue(v: unknown, depth: number): unknown {
  if (depth > 6) return v
  if (Array.isArray(v)) {
    return v.map((item) => scrubValue(item, depth + 1))
  }
  if (v && typeof v === 'object') {
    return scrubObject(v as Record<string, unknown>, depth + 1)
  }
  return v
}

function scrubObject(obj: Record<string, unknown>, depth = 0): Record<string, unknown> {
  if (depth > 6) return obj
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    out[k] = SENSITIVE_KEY.test(k) ? REDACTED : scrubValue(v, depth)
  }
  return out
}

function scrubHeaders(headers: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(headers)) {
    out[k] = SENSITIVE_HEADER.test(k) ? REDACTED : v
  }
  return out
}

// Session-Replay (rrweb) stack-overflow guard.
//
// Sentry Session Replay records the DOM through a bundled rrweb. On pages that
// host shadow-DOM web components — e.g. the Google Places Autocomplete element
// on /store, and similar custom elements on /tutorials — rrweb's attachShadow
// hook can recurse into itself with no depth bound and blow the stack. It
// surfaces as a RangeError "Maximum call stack size exceeded" thrown from
// inside the SDK's own recording path (frames in rrweb / attachShadow /
// attachShadowHook), NOT from our application code. The primary fix is the
// @sentry/nextjs bump, which carries the upstream rrweb attachShadow-recursion
// fix; this beforeSend guard is defense-in-depth so any framed variant that
// still slips through is dropped here instead of (a) spamming the Sentry board
// and (b) uploading a full Session Replay on every occurrence, which is exactly
// what burns the replay quota (see replaysOnErrorSampleRate note in
// sentry.server.config.ts / instrumentation-client.ts).
//
// Deliberately NARROW. We drop ONLY a RangeError whose message is the
// stack-overflow message AND whose own stack references rrweb /
// attachShadow(Hook). Genuine application stack overflows carry our own frames
// and no rrweb frame — e.g. the PortableText self-recursion (#1138) — so they
// are never matched and stay fully visible.
const RRWEB_SHADOW_FRAME = /rrweb|attachShadowHook|attachShadow/i
const STACK_OVERFLOW_MESSAGE = 'Maximum call stack size exceeded'

function isReplayShadowRecursion(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!Array.isArray(values)) return false
  // The message, the type, and the rrweb frames must all belong to the SAME
  // exception so we never drop an unrelated RangeError that merely shares a
  // chained exception with some rrweb frame.
  return values.some((value) => {
    if (value?.type !== 'RangeError') return false
    if (!value.value?.includes(STACK_OVERFLOW_MESSAGE)) return false
    const frames = value.stacktrace?.frames
    if (!Array.isArray(frames)) return false
    return frames.some(
      (frame) =>
        RRWEB_SHADOW_FRAME.test(frame?.filename ?? '') ||
        RRWEB_SHADOW_FRAME.test(frame?.function ?? ''),
    )
  })
}

// Client-only flood throttle for stack-overflow RangeErrors.
//
// A stack overflow fires window.onerror in a tight loop, and on iOS WebKit/JSC
// the error carries a DEGENERATE single-frame stack (filename literally the
// string 'undefined', no function name) — so the rrweb-frame guard above can
// never match it (THEDUCHY-WEBSITE-54; root cause is Google Translate's
// injected script, NOT rrweb — see the tagging note below), and every repeat is sent, overflowing
// the SDK's client transport (the queue_overflow / ratelimit_backoff drops) and
// burning the 0.5-sampled Session Replay quota. We KEEP THE FIRST occurrence per
// distinct signature per page load — full signal: breadcrumbs, replay, release
// tag, still PII-scrubbed below — and drop the within-load flood.
//
// typeof window guards it to the BROWSER only: scrubSentryEvent is shared with
// the server AND edge Sentry configs, where a module-scoped Set lives for the
// whole process and would silently dedupe distinct users'/requests' overflows
// after the first. On the client the module re-initializes per page load, so the
// Set is naturally scoped to one load. Scoped to RangeError + the overflow
// message only, so the Set can neither grow unbounded nor drop unrelated errors.
// On iOS/JSC every overflow collapses to the same degenerate signature (so a
// second distinct-root-cause overflow in the same load is suppressed — but JSC
// gives it no distinguishable frame anyway, so no signal is lost); on V8 a
// genuinely distinct overflow carries its own top frame → own signature → kept.
const seenStackOverflowSignatures = new Set<string>()

function stackOverflowValue(event: ErrorEvent) {
  return (event.exception?.values ?? []).find(
    (v) => v?.type === 'RangeError' && (v.value ?? '').includes(STACK_OVERFLOW_MESSAGE),
  )
}

/** Signature = type · message · top (crashing) frame. Sentry orders frames
 *  leaf-last, so the crashing frame is the last element. */
function stackOverflowSignature(value: NonNullable<ReturnType<typeof stackOverflowValue>>): string {
  const frames = value.stacktrace?.frames
  const top = Array.isArray(frames) && frames.length ? frames[frames.length - 1] : undefined
  return [value.type, value.value, top?.filename, top?.lineno, top?.colno].join('|')
}

// Browser-injected-script + machine-translation TAGGING (tag only — never drop).
//
// THEDUCHY-WEBSITE-54/-55/-59/-5A/-5B/-5J/-5K ("Maximum call stack size
// exceeded") and the "Error: La" / "Error: pa" issues are thrown by Google
// Translate's own script, which Chrome for iOS evaluates into the page when a
// member has (auto-)translate on — proven 2026-10-27 by mapping every frame
// (228:63 `Fk(` in Dk, 228:409 `Dk(` in Fk, 515:344 `Error("La")`, …) onto
// Google's el_main script. WebKit names such evaluated scripts after the
// DOCUMENT, so their frames read `app:///tutorials:228:63` — never
// `app:///_next/…`, where all of our bundled code lives. The root-cause fix is
// `<meta name="google" content="notranslate">` in the root layout.
//
// Two tags make the effect measurable without hiding anything:
//   stack_origin   — 'bundle' (every framed frame is ours, under /_next/),
//                    'non_bundle' (none are: document-URL frames = browser-
//                    injected code, inline scripts, or third-party scripts the
//                    SDK normalized to app://), 'mixed', or 'none' (frameless,
//                    e.g. JSC's degenerate `undefined:195:41`).
//   page_translated — 'true' when the live DOM carries Google Translate's own
//                    markers (an html.translated-ltr/rtl class or its
//                    <font style="vertical-align: inherit"> wrappers). A bare
//                    <font> is NOT enough: admin-written announcement HTML can
//                    contain one.
// Deliberately NOT a drop rule: non_bundle also matches first-party inline
// scripts (e.g. /auth's multi-line inline <script>s) and third-party SDKs
// (Stripe, Accept.js, Maps), so dropping on it would silently hide real errors.
type StackOrigin = 'bundle' | 'non_bundle' | 'mixed' | 'none'

export function classifyStackOrigin(event: ErrorEvent): StackOrigin {
  let ours = 0
  let other = 0
  for (const value of event.exception?.values ?? []) {
    for (const frame of value?.stacktrace?.frames ?? []) {
      const file = frame?.filename
      if (!file || file === 'undefined' || file === '<anonymous>') continue
      if (file.startsWith('app:///_next/') || file.includes('/_next/static/')) ours++
      else other++
    }
  }
  if (ours === 0 && other === 0) return 'none'
  if (other === 0) return 'bundle'
  if (ours === 0) return 'non_bundle'
  return 'mixed'
}

type TranslationProbe = {
  documentElement: { classList: { contains(token: string): boolean } } | null
  querySelector(selector: string): unknown
}

/** True when Google Translate has rewritten the page (its own markers only). */
export function isPageMachineTranslated(doc: TranslationProbe | undefined): boolean {
  if (!doc) return false
  try {
    const cls = doc.documentElement?.classList
    if (cls && (cls.contains('translated-ltr') || cls.contains('translated-rtl'))) return true
    return doc.querySelector('font[style*="vertical-align: inherit"]') != null
  } catch {
    return false
  }
}

function tagEvent(event: ErrorEvent, key: string, value: string): void {
  event.tags = { ...(event.tags ?? {}), [key]: value }
}

/**
 * Sentry `beforeSend` hook: scrubs PII / payment / secret fields from event
 * payloads before they leave the runtime. Defense in depth alongside
 * `sendDefaultPii: false` — covers fields we may have explicitly attached
 * (e.g. via `Sentry.setExtra`) and request bodies.
 */
export function scrubSentryEvent(event: ErrorEvent, _hint?: EventHint): ErrorEvent | null {
  // Drop Session-Replay-induced rrweb attachShadow stack overflows outright —
  // before any scrubbing, since the whole event is discarded.
  if (isReplayShadowRecursion(event)) return null

  // Client-only: keep the FIRST stack-overflow per signature per page load and
  // drop the within-load flood (the frame-less iOS/JSC #54 the rrweb guard above
  // cannot match). typeof window keeps this off the server/edge, where the
  // module-scoped Set would wrongly dedupe across users (see note above).
  if (typeof window !== 'undefined') {
    const overflow = stackOverflowValue(event)
    if (overflow) {
      const sig = stackOverflowSignature(overflow)
      if (seenStackOverflowSignatures.has(sig)) return null
      seenStackOverflowSignatures.add(sig)
    }
    // Tag (never drop) — see "Browser-injected-script + machine-translation
    // TAGGING" above. Client-only: the server has no document and its frames
    // are never document URLs.
    if (event.exception?.values?.length) tagEvent(event, 'stack_origin', classifyStackOrigin(event))
    if (isPageMachineTranslated(typeof document !== 'undefined' ? document : undefined)) {
      tagEvent(event, 'page_translated', 'true')
    }
  }

  if (event.request) {
    const req = event.request as Record<string, unknown>
    if (req.headers && typeof req.headers === 'object') {
      req.headers = scrubHeaders(req.headers as Record<string, unknown>)
    }
    if (req.cookies) req.cookies = REDACTED
    if (typeof req.data === 'object' && req.data !== null) {
      req.data = scrubObject(req.data as Record<string, unknown>)
    } else if (typeof req.data === 'string') {
      try {
        req.data = JSON.stringify(scrubObject(JSON.parse(req.data)))
      } catch {
        // leave as-is if it isn't JSON
      }
    }
  }

  if (event.user) {
    // user.id is fine; remove email / username / ip
    const u = event.user as Record<string, unknown>
    delete u.email
    delete u.username
    delete u.ip_address
  }

  if (event.extra && typeof event.extra === 'object') {
    event.extra = scrubObject(event.extra as Record<string, unknown>)
  }
  if (event.contexts && typeof event.contexts === 'object') {
    event.contexts = scrubObject(event.contexts as Record<string, unknown>) as ErrorEvent['contexts']
  }

  return event
}

export function scrubSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    breadcrumb.data = scrubObject(breadcrumb.data as Record<string, unknown>)
  }
  return breadcrumb
}
